import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

interface SectionCardProps {
  id?: string;
  icon: React.ReactElement;
  title: string;
  subtitle?: React.ReactNode;
  /** Unsaved changes in the section: shows the save/discard bar. */
  dirtyCount?: number;
  saving?: boolean;
  /** Disables save (e.g. while values are being recalculated). */
  saveDisabled?: boolean;
  onSave?: () => void;
  onDiscard?: () => void;
  /** Label for the save button (e.g. "Crear" while creating). */
  saveLabel?: string;
  /** Header actions shown when there is nothing to save. */
  actions?: React.ReactNode;
  createdAt?: string | null;
  updatedAt?: string | null;
  children: React.ReactNode;
}

const formatStamp = (value?: string | null) =>
  value ? dayjs(value).format("DD/MM/YYYY HH:mm") : null;

/** Card for one block of the page: header, save bar and footer stamps. */
const SectionCard: React.FC<SectionCardProps> = ({
  id,
  icon,
  title,
  subtitle,
  dirtyCount = 0,
  saving = false,
  saveDisabled = false,
  onSave,
  onDiscard,
  saveLabel,
  actions,
  createdAt,
  updatedAt,
  children,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const hasChanges = dirtyCount > 0 || Boolean(saveLabel);
  const created = formatStamp(createdAt);
  const updated = formatStamp(updatedAt);

  return (
    <Paper
      id={id}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${
          dirtyCount > 0 ? theme.palette.warning.main : theme.palette.divider
        }`,
        transition: "border-color .2s",
        overflow: "hidden",
        scrollMarginTop: 16,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 }, pb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: theme.palette.action.hover,
              color: "text.primary",
              "& svg": { fontSize: 20 },
            }}
          >
            {icon}
          </Box>
          <Box minWidth={0}>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ fontFamily: "Montserrat", lineHeight: 1.25 }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.disabled"
                component="div"
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>

        {hasChanges && onSave ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flexShrink: 0 }}
          >
            {dirtyCount > 0 && (
              <Typography
                variant="caption"
                sx={{
                  px: 1,
                  py: 0.4,
                  borderRadius: 10,
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.warning.main, 0.2),
                  color: "text.primary",
                }}
              >
                {t("inlineEdit.pendingCount", {
                  count: dirtyCount,
                  defaultValue:
                    dirtyCount === 1
                      ? "{{count}} cambio sin guardar"
                      : "{{count}} cambios sin guardar",
                })}
              </Typography>
            )}
            {onDiscard && (
              <Button
                size="small"
                color="inherit"
                onClick={onDiscard}
                disabled={saving}
              >
                {t("inlineEdit.discard", "Descartar")}
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={onSave}
              disabled={saving || saveDisabled}
              startIcon={
                saving ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
            >
              {saveLabel ?? t("common.save", "Save")}
            </Button>
          </Stack>
        ) : (
          actions && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flexShrink: 0 }}
            >
              {actions}
            </Stack>
          )
        )}
      </Stack>

      <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, pt: 1 }}>
        {children}
      </Box>

      {(created || updated) && (
        <>
          <Divider />
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            sx={{ px: { xs: 2, md: 3 }, py: 1.25 }}
          >
            {created && (
              <Typography variant="caption" color="text.secondary">
                {t("briefingsView.addedOn", "Added on")}: {created}
              </Typography>
            )}
            {updated && (
              <Typography variant="caption" color="text.secondary">
                {t("briefingsView.updatedOn", "Last updated")}: {updated}
              </Typography>
            )}
          </Stack>
        </>
      )}
    </Paper>
  );
};

export default SectionCard;
